import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Flex, Form, Spin } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  usePatientsServiceMarkAsPatientEndpoint,
  usePersonsServiceGetPersonByIdEndpointKey,
  useTreatmentServiceCheckPregnancyHistoryEndpointKey,
  useTreatmentServiceCreateConsultationEndpoint,
  useTreatmentServiceCreateConsultationExaminationEndpoint,
  useTreatmentServiceCreateConsultationInvestigationDemandLinkEndpoint,
  useTreatmentServiceCreateConsultationTreatmentPlanEndpoint,
  useTreatmentServiceCreatePotentialContactEndpoint,
  useTreatmentServiceGetConsultationListEndpointKey,
  useTemplatesServiceCreateSystemTemplateEndpoint,
  useTemplatesServiceUpdateSystemTemplateEndpoint,
  useTemplatesServiceGetSystemTemplateEndpoint,
  useTemplatesServiceGetSystemTemplateListEndpointKey
} from 'src/api/queries'
import {
  ApiError,
  ConsultationType,
  ContentType,
  Permissions
} from 'src/api/requests'
import {
  FormFooter,
  message,
  notification,
  PageBreadcrumbs,
  SaveConfirm,
  Text
} from 'src/components'
import {
  PersonConsultationsTreatmentSection,
  ConsultationGeneralInformationCard,
  ActiveSimeTbTreatmentCard,
  ConsultationTbScreeningCard,
  tbScreeningToForm,
  tbScreeningToPayload,
  ConsultationFinalDiagnosticCard,
  ConsultationFormCard,
  DraftConsultationsModal
} from 'src/features/persons/components'
import {
  ContactsLayout,
  ContactsList,
  ContactsSection
} from 'src/features/persons/components/PersonalDetails/components/ContactsForm'
import { ConsultationLaboratoryCard } from 'src/features/persons/components/PersonConsultations/ConsultationLaboratoryCard'
import {
  normalizeCron,
  TreatmentInquiryItem
} from 'src/features/persons/components/PersonConsultations/Treatments'
import { usePersonBreadcrumb } from 'src/features/persons/hooks'
import { ValueLabelType } from 'src/features/persons/types'
import { assessmentToPayload } from 'src/features/persons/utils'
import {
  EXISTING_PREGNANCY_PLAN,
  EXISTING_TREATMENT_PLAN
} from 'src/features/persons/utils/constants'
import { useFormStyles } from 'src/features/stock/styles'
import {
  useExpiryAutosave,
  useScrollToFirstError,
  useUser,
  ValidationError
} from 'src/hooks'
import {
  formatDate,
  hourMinutesSecondFormat,
  yearMonthDaySlash
} from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, parseDates, toDayjs } from 'src/utils'

export const AddConsultationPage = () => {
  const { t } = useLocales()
  const { personId, draftId } = useParams()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { hasPermission } = useUser()
  const queryClient = useQueryClient()

  const isPatient = pathname.includes('patients')

  const [form] = Form.useForm()

  const { styles } = useFormStyles()

  const { data: draftData, isLoading: isLoadingDraft } =
    useTemplatesServiceGetSystemTemplateEndpoint(
      { id: String(draftId) },
      [String(draftId)],
      { enabled: !!draftId }
    )

  const [isContactsOpen, setIsContactsOpen] = useState(false)
  const [isDraftModalOpen, setIsDraftModalOpen] = useState(false)

  const onToggle = () => setIsContactsOpen((prev) => !prev)

  const { onScrollToError } = useScrollToFirstError(form)

  const hasLaboratory = Form.useWatch('hasLaboratory', form)
  // Bumped by the TB screening to open the TB investigation request.
  const [tbRequestSignal, setTbRequestSignal] = useState(0)
  const [tbPreselectCategoryIds, setTbPreselectCategoryIds] = useState<
    string[]
  >([])
  const hasTreatment = Form.useWatch('hasTreatment', form)
  const hasConsultationType = !!Form.useWatch(
    ['generalInformation', 'consultationType'],
    form
  )
  const hasExamination = Form.useWatch('hasExamination', form)
  const hasContacts = !!Form.useWatch('contactsList', form)
  const hasCheckedContacts = Form.useWatch('hasContacts', form)
  const hasFinalDiagnostic = Form.useWatch('hasFinalDiagnostic', form)
  const hasPregnancySelected = Form.useWatch(
    ['form', 'anamnesisSection', 'hasPregnancySelected'],
    form
  )

  const treatmentPlanId = form.getFieldValue(EXISTING_TREATMENT_PLAN)
  const pregnancyPlanId = form.getFieldValue(EXISTING_PREGNANCY_PLAN)

  const { breadcrumbItems, fullName, person, isLoadingPerson } =
    usePersonBreadcrumb(personId)

  const { mutate, isPending } = useTreatmentServiceCreateConsultationEndpoint({
    onError: (err: ApiError) => formErrorHandler(err.body.errors),
    onSuccess: async (data, payload) => {
      message.success(t('consultations.success_create'))
      if (hasPregnancySelected) {
        queryClient.invalidateQueries({
          queryKey: [useTreatmentServiceCheckPregnancyHistoryEndpointKey]
        })
      }

      queryClient.invalidateQueries({
        queryKey: [useTreatmentServiceGetConsultationListEndpointKey]
      })

      if (hasTreatment) await updateTarvCode()

      const formData = form.getFieldsValue(true)

      const createFormPayload = {
        form: {
          ...formData.form,
          anamnesisSection: {
            ...formData?.form?.anamnesisSection,
            familyMedicalAnamnesis:
              formData?.form?.anamnesisSection?.familyMedicalAnamnesis ?? {},
            substanceConsumption:
              formData?.form?.anamnesisSection?.substanceConsumption ?? {},
            vaccines: formData?.form?.anamnesisSection?.vaccines ?? {},
            surgeriesInAnamnesis:
              formData?.form?.anamnesisSection?.surgeriesInAnamnesis ||
              undefined,
            allergies: formData?.form?.anamnesisSection?.allergies ?? {},
            currentMedication:
              formData?.form?.anamnesisSection?.currentMedication || undefined,
            reproductiveAndSexualHealth:
              formData?.form?.anamnesisSection?.reproductiveAndSexualHealth ??
              {},
            pregnancy: formData?.form?.anamnesisSection?.pregnancy ?? {},

            coveredDiseasesAndIllnessesIds:
              formData?.form?.anamnesisSection?.coveredDiseasesAndIllnessesIds?.map(
                (item: ValueLabelType) => item.value
              ) ?? []
          },
          physicalExaminationSection: {
            ...formData?.form?.physicalExaminationSection,
            constitutionalType:
              formData?.form?.physicalExaminationSection?.constitutionalType ??
              {},
            cutaneousTeguments:
              formData?.form?.physicalExaminationSection?.cutaneousTeguments ??
              {},
            oropharynx:
              formData?.form?.physicalExaminationSection?.oropharynx ?? {},
            genitalAnalRegionAssessment:
              formData?.form?.physicalExaminationSection
                ?.genitalAnalRegionAssessment ?? {}
          },
          examinationDate: dayjs(
            `${payload.requestBody.generalInformation.consultationDate} ${payload.requestBody.generalInformation.consultationHour}`
          ).format()
        }
      }

      hasExamination &&
        createConsultationForm({
          requestBody: {
            ...createFormPayload,
            consultationId: String(data.id),
            consultationType: payload.requestBody.generalInformation
              .consultationType as ConsultationType
          }
        })

      const investigationDemandIds = form.getFieldValue(
        'investigationDemandIds'
      )

      if (hasLaboratory && investigationDemandIds?.length > 0) {
        addLaboratory({
          id: String(data.id),
          requestBody: { investigationDemandIds }
        })
      }

      if (!treatmentPlanId && hasTreatment) {
        const values = await form.validateFields()

        const medicine = values?.medicine.map((item: TreatmentInquiryItem) => ({
          internationalNameId: item?.internationalNameId,
          productFormId: item?.productFormId?.id,
          amount: item?.amount,
          scheduleExpression: normalizeCron(item?.cron),
          startDate: dayjs(item?.startDate).format(yearMonthDaySlash),
          endDate: toDayjs(item?.endDate)?.format(yearMonthDaySlash)
        }))

        addTreatment({
          id: String(data.id),
          requestBody: {
            treatmentTypeId: values?.treatment_type,
            startDate: formatDate(
              values?.treatment_start_date,
              yearMonthDaySlash
            ),
            endDate: toDayjs(values?.treatment_end_date)?.format(
              yearMonthDaySlash
            ),
            prescribedTreatments: medicine,
            isUrgent: values?.isUrgent,
            treatmentTypeRegimenId: values?.treatmentTypeRegimenId,
            preventionTreatmentContingentTypeId:
              values?.preventionTreatmentContingentTypeId
          }
        })
      }

      if (hasContacts) {
        const { contactsList } = await form.validateFields()

        createContact({
          consultationId: String(data.id),
          requestBody: { contacts: contactsList }
        })
      }

      navigate(
        isPatient
          ? `${paths.patients.details(String(personId))}${paths.patients.consultations.root()}`
          : `${paths.persons.details(String(personId))}${paths.persons.consultations.root()}`
      )
    }
  })

  const { mutateAsync: addTreatment } =
    useTreatmentServiceCreateConsultationTreatmentPlanEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors)
    })

  const { mutate: addLaboratory } =
    useTreatmentServiceCreateConsultationInvestigationDemandLinkEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors)
    })

  const { mutateAsync: createConsultationForm } =
    useTreatmentServiceCreateConsultationExaminationEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors)
    })

  const { mutateAsync: updatePatientCodes } =
    usePatientsServiceMarkAsPatientEndpoint()

  // TARV code is editable in the treatment section - persist it on the patient
  // without failing the consultation save
  const updateTarvCode = async () => {
    const tarvCode = String(form.getFieldValue('tarvCode') ?? '').trim()

    if (!person?.simeCode || tarvCode === (person.tarvCode ?? '')) return

    try {
      await updatePatientCodes({
        id: String(personId),
        requestBody: { simeCode: person.simeCode, tarvCode: tarvCode || null }
      })
      queryClient.invalidateQueries({
        queryKey: [usePersonsServiceGetPersonByIdEndpointKey]
      })
    } catch {
      message.error(t('common.tarv_code_update_error'))
    }
  }

  const { mutate: createContact } =
    useTreatmentServiceCreatePotentialContactEndpoint({
      onError: (err: ApiError) => formErrorHandler(err.body.errors)
    })

  const onTemplateMutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [useTemplatesServiceGetSystemTemplateListEndpointKey]
    })
  }

  const { mutate: createTemplate, isPending: isCreatingDraft } =
    useTemplatesServiceCreateSystemTemplateEndpoint({
      onSuccess: (data) => {
        onTemplateMutationSuccess()
        message.success(t('consultations.draft_saved'))
        navigate(
          isPatient
            ? paths.patients.consultations.draft(
                String(personId),
                String(data.id)
              )
            : paths.persons.consultations.draft(
                String(personId),
                String(data.id)
              )
        )
      },
      onError: (err: ApiError) => formErrorHandler(err.body.errors)
    })

  const { mutate: updateTemplate, isPending: isUpdatingDraft } =
    useTemplatesServiceUpdateSystemTemplateEndpoint({
      onSuccess: () => {
        onTemplateMutationSuccess()
        message.success(t('consultations.draft_updated'))
      },
      onError: (err: ApiError) => formErrorHandler(err.body.errors)
    })

  const onBackClick = () => {
    navigate(
      isPatient
        ? `${paths.patients.details(String(personId))}${paths.patients.consultations.root()}`
        : `${paths.persons.details(String(personId))}${paths.persons.consultations.root()}`
    )
  }

  const onSave = async () => {
    try {
      const values = await form.validateFields()

      const payload = {
        personId: String(personId),
        generalInformation: {
          consultationType: values.generalInformation.consultationType,
          consultationDate: formatDate(
            values.generalInformation.consultationDate,
            yearMonthDaySlash
          ),
          consultationHour: dayjs
            .utc(values.generalInformation.consultationHour)
            .format(hourMinutesSecondFormat),
          lastConsultationDate: form.getFieldValue('lastConsultationDate')
        },
        // A single diagnosis: the preliminary information is no longer sent.
        assessment: assessmentToPayload(values.assessment),
        ...(treatmentPlanId && {
          existingTreatmentPlanId: treatmentPlanId
        }),
        ...(pregnancyPlanId && {
          existingPregnancyMonitoringId: pregnancyPlanId
        }),
        tbScreening: tbScreeningToPayload(values.tbScreening),
        form: undefined
      }

      mutate({
        requestBody: payload
      })
    } catch (error) {
      onScrollToError(error as ValidationError)
    }
  }

  const onSaveDraft = () => {
    const values = form.getFieldsValue(true)

    const content = {
      personId: String(personId),
      generalInformation: values.generalInformation
        ? {
            consultationType:
              values.generalInformation.consultationType ?? undefined,
            consultationDate: values.generalInformation.consultationDate
              ? formatDate(
                  values.generalInformation.consultationDate,
                  yearMonthDaySlash
                )
              : undefined,
            consultationHour: values.generalInformation.consultationHour
              ? dayjs
                  .utc(values.generalInformation.consultationHour)
                  .format(hourMinutesSecondFormat)
              : undefined,
            lastConsultationDate: form.getFieldValue('lastConsultationDate')
          }
        : undefined,
      assessment: values.assessment,
      form: values.form,
      hasLaboratory: values.hasLaboratory,
      hasTreatment: values.hasTreatment,
      hasExamination: values.hasExamination,
      hasContacts: values.hasContacts,
      hasFinalDiagnostic: values.hasFinalDiagnostic,
      tbScreening: tbScreeningToPayload(values.tbScreening),
      contactsList: values.contactsList,
      investigationDemandIds: values.investigationDemandIds,
      medicine: values.medicine,
      treatment_type: values.treatment_type,
      treatment_start_date: values.treatment_start_date,
      treatment_end_date: values.treatment_end_date,
      isUrgent: values.isUrgent,
      treatmentTypeRegimenId: values.treatmentTypeRegimenId,
      preventionTreatmentContingentTypeId:
        values.preventionTreatmentContingentTypeId,
      ...(treatmentPlanId && { existingTreatmentPlanId: treatmentPlanId }),
      ...(pregnancyPlanId && {
        existingPregnancyMonitoringId: pregnancyPlanId
      })
    }

    if (draftId) {
      updateTemplate({
        id: String(draftId),
        requestBody: {
          name: draftData?.name ?? t('consultations.draft'),
          description: draftData?.description,
          isPublic: true,
          content
        }
      })
    } else {
      createTemplate({
        requestBody: {
          name: `${t('consultations.draft')} - ${fullName ?? personId}`,
          isPublic: true,
          type: ContentType.CONSULTATION_DRAFT,
          content
        }
      })
    }
  }

  // Autosave the consultation as a draft ~1 minute before the session
  // (access token) expires, so unsaved form data is not lost on logout.
  useExpiryAutosave({
    onAutosave: () => {
      if (!form.isFieldsTouched()) return

      onSaveDraft()
      notification.info({
        message: t('consultations.autosave_session_expiring'),
        description: t('consultations.autosave_draft_saved')
      })
    }
  })

  useEffect(() => {
    if (draftId && draftData?.content) {
      const content = draftData.content

      form.setFieldsValue({
        generalInformation: content.generalInformation
          ? {
              consultationType: content.generalInformation.consultationType,
              consultationDate: content.generalInformation.consultationDate
                ? dayjs(content.generalInformation.consultationDate)
                : undefined,
              consultationHour: content.generalInformation.consultationHour
                ? dayjs(
                    content.generalInformation.consultationHour,
                    hourMinutesSecondFormat
                  )
                : undefined
            }
          : undefined,
        // Drafts saved before the single diagnosis card keep it as preliminary information.
        assessment: parseDates(
          content.assessment ??
            (content.preliminaryInformation && {
              finalDiagnosticIds:
                content.preliminaryInformation.preliminaryDiagnosticIds,
              opportunisticDiseaseIds:
                content.preliminaryInformation.opportunisticDiseaseIds,
              dateOfDiseaseOnset:
                content.preliminaryInformation.dateOfDiseaseOnset
            })
        ),
        form: content.form,
        hasLaboratory: content.hasLaboratory,
        hasTreatment: content.hasTreatment,
        hasExamination: content.hasExamination,
        hasContacts: content.hasContacts,
        hasFinalDiagnostic:
          content.hasFinalDiagnostic || content.hasPreliminaryInformation,
        tbScreening: tbScreeningToForm(content.tbScreening),
        contactsList: content.contactsList,
        investigationDemandIds: content.investigationDemandIds,
        // The draft stores the date pickers' values as JSON strings; the
        // pickers need dayjs back.
        medicine: parseDates(content.medicine),
        treatment_type: content.treatment_type,
        treatment_start_date: parseDates(content.treatment_start_date),
        treatment_end_date: parseDates(content.treatment_end_date),
        isUrgent: content.isUrgent,
        treatmentTypeRegimenId: content.treatmentTypeRegimenId,
        preventionTreatmentContingentTypeId:
          content.preventionTreatmentContingentTypeId
      })
    }
  }, [draftData, draftId])

  return (
    <Spin spinning={isLoadingPerson || isLoadingDraft}>
      <PageBreadcrumbs title={fullName} items={breadcrumbItems} showBack>
        <Flex vertical gap={24} className={styles.form}>
          <Flex justify="space-between">
            <Text className="modal-title">
              {draftId
                ? t('consultations.draft')
                : t('consultations.register_new_consultation')}
            </Text>

            <Flex gap={8}>
              {!draftId && (
                <Button onClick={() => setIsDraftModalOpen(true)}>
                  {t('consultations.from_draft')}
                </Button>
              )}

              <SaveConfirm
                title={`${t('consultations.save_as_draft')}?`}
                onConfirm={onSaveDraft}
                placement="leftBottom"
                okButtonProps={{
                  loading: isCreatingDraft || isUpdatingDraft
                }}
              >
                <Button>{t('consultations.save_as_draft')}</Button>
              </SaveConfirm>
            </Flex>
          </Flex>

          <Form
            layout="vertical"
            form={form}
            requiredMark={false}
            scrollToFirstError
          >
            <Flex vertical gap={24}>
              <Card>
                <ConsultationGeneralInformationCard form={form} />
              </Card>

              <ActiveSimeTbTreatmentCard
                personId={personId}
                idnp={person?.idnp}
              />

              <Card>
                <ConsultationTbScreeningCard
                  form={form}
                  personId={personId}
                  onRequestLaboratory={(categoryIds) => {
                    form.setFieldValue('hasLaboratory', true)
                    setTbPreselectCategoryIds(categoryIds)
                    setTbRequestSignal((signal) => signal + 1)
                  }}
                />
              </Card>

              {hasConsultationType && hasExamination && (
                <Card>
                  <ConsultationFormCard form={form} />
                </Card>
              )}
              {hasLaboratory && (
                <ConsultationLaboratoryCard
                  isVisible={!!hasLaboratory}
                  form={form}
                  openTbRequestSignal={tbRequestSignal}
                  tbPreselectCategoryIds={tbPreselectCategoryIds}
                />
              )}

              {hasFinalDiagnostic && (
                <Card>
                  <ConsultationFinalDiagnosticCard form={form} />
                </Card>
              )}

              {hasTreatment && (
                <PersonConsultationsTreatmentSection
                  person={person}
                  form={form}
                />
              )}

              {hasPermission(
                Permissions.TREATMENT_CONSULTATION_VIEW_POTENTIAL_CONTACTS_INSTITUTION
              ) &&
                hasCheckedContacts && (
                  <Card>
                    <ContactsLayout onToggle={onToggle}>
                      <Form.Item name="contactsList">
                        <ContactsList form={form} />
                      </Form.Item>
                    </ContactsLayout>
                  </Card>
                )}
              {isContactsOpen && (
                <ContactsSection onCancel={onToggle} form={form} />
              )}
            </Flex>

            <FormFooter
              onCancel={onBackClick}
              onSave={onSave}
              saveConfirmTitle={`${t('persons.save')}?`}
              saveLabel={t('persons.save')}
              loading={isPending}
            />
          </Form>
        </Flex>
      </PageBreadcrumbs>

      {isDraftModalOpen && (
        <DraftConsultationsModal onCancel={() => setIsDraftModalOpen(false)} />
      )}
    </Spin>
  )
}
